import React, { useEffect, useState, RefObject } from 'react';
import { contextMenuEmitter, ContextMenuContent } from './GlobalContextMenu';
import { ExamineModal } from './ExamineModal';
import {
  getIslandExamineText,
  getFishingSpotExamineText,
  getShipExamineText,
  getResourceNodeExamineText,
  getDebrisExamineText,
  getRiftScarExamineText,
  getSanctumContainerExamineText,
  getWorldExamineText,
  ExamineText,
} from '../utils/ExamineFlavorText';

interface ContextMenuRendererProps {
  gameRef?: RefObject<any>;
}

/**
 * Format fishing spot type to display name
 */
const formatFishingSpotType = (spotType: string): string => {
  const typeMap: Record<string, string> = {
    'coral_reef': 'Coral Reef',
    'kelp_forest': 'Kelp Forest',
    'shallow_waters': 'Shallow Waters',
  };
  return typeMap[spotType] || spotType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

/**
 * Simple context menu renderer that listens to the global context menu emitter
 */
export const ContextMenuRenderer: React.FC<ContextMenuRendererProps> = ({ gameRef }) => {
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    content: ContextMenuContent | null;
  }>({
    visible: false,
    x: 0,
    y: 0,
    content: null,
  });
  const [examineModal, setExamineModal] = useState<{
    visible: boolean;
    data: ExamineText | null;
  }>({
    visible: false,
    data: null,
  });

  useEffect(() => {
    const unsubscribe = contextMenuEmitter.on((content, x, y) => {
      // Get the game canvas's position on the page to account for viewport offset
      const canvas = gameRef?.current?.canvas;
      let viewportX = x;
      let viewportY = y;
      
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        viewportX = x + rect.left;
        viewportY = y + rect.top;
      }
      
      console.log(`ContextMenuRenderer: Setting visible=true for type: ${content.type} at screen (${x}, ${y}), viewport (${viewportX}, ${viewportY})`);
      setContextMenu({
        visible: true,
        x: viewportX,
        y: viewportY,
        content,
      });
    });

    const unsubscribeHide = contextMenuEmitter.onHide(() => {
      console.log(`ContextMenuRenderer: Hiding context menu`);
      setContextMenu({
        visible: false,
        x: 0,
        y: 0,
        content: null,
      });
    });

    return () => {
      unsubscribe();
      unsubscribeHide();
    };
  }, [gameRef]);

  const handleExamine = (examineData: ExamineText) => {
    setExamineModal({ visible: true, data: examineData });
    contextMenuEmitter.hide();
  };

  if (!contextMenu.visible || !contextMenu.content) {
    return (
      <>
        {examineModal.visible && (
          <ExamineModal
            examineData={examineModal.data}
            onClose={() => setExamineModal({ visible: false, data: null })}
          />
        )}
      </>
    );
  }

  console.log(`ContextMenuRenderer: RENDERING at (${contextMenu.x}, ${contextMenu.y}), type: ${contextMenu.content.type}`);

  return (
    <div
      className="global-context-menu"
      style={{
        position: 'fixed',
        left: contextMenu.x,
        top: contextMenu.y,
        zIndex: 10001,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {contextMenu.content.type === 'island' && (
        <>
          <div className="context-menu-header">
            {contextMenu.content.data?.permanentName || contextMenu.content.data?.islandName || 'Island'}
          </div>
          <button className="context-menu-item" onClick={() => {
            console.log('👉 Context menu: Research clicked');
            const scene = gameRef?.current?.scene.getScene('OceanScene') as any;
            const content = contextMenu.content;
            if (scene && content?.data?.islandId && content?.data?.islandPosition) {
              // Emit event to open research panel
              scene.events.emit('island:openResearch', {
                islandId: content.data.islandId,
                islandPosition: content.data.islandPosition,
              });
            }
            contextMenuEmitter.hide();
          }}>Research</button>
          <button className="context-menu-item" onClick={() => handleExamine(getIslandExamineText({
            islandId: contextMenu.content?.data?.islandId,
            permanentName: contextMenu.content?.data?.permanentName,
            isPermanent: contextMenu.content?.data?.isPermanent,
            biome: contextMenu.content?.data?.biome,
            size: contextMenu.content?.data?.size,
          }))}>Examine</button>
          <button className="context-menu-item" onClick={() => {
            console.log('👉 Context menu: Dock clicked');
            const scene = gameRef?.current?.scene.getScene('OceanScene') as any;
            const content = contextMenu.content;
            if (scene && content?.data?.islandId) {
              scene.dockAtIsland(content.data.islandId);
            }
            contextMenuEmitter.hide();
          }}>Dock</button>
          <button className="context-menu-item" onClick={() => contextMenuEmitter.hide()}>Cancel</button>
        </>
      )}
      {contextMenu.content.type === 'ship' && (
        <>
          <div className="context-menu-header">Enemy Ship</div>
          <button className="context-menu-item" onClick={() => {
            console.log('👉 Context menu: Investigate clicked');
            const scene = gameRef?.current?.scene.getScene('OceanScene') as any;
            const content = contextMenu.content;
            if (scene && content?.data?.shipId && content?.data?.shipData) {
              scene.events.emit('ship:openInvestigation', {
                shipId: content.data.shipId,
                shipData: content.data.shipData,
              });
            }
            contextMenuEmitter.hide();
          }}>Investigate</button>
          <button className="context-menu-item" onClick={() => {
            console.log('👉 Context menu: Engage clicked');
            const scene = gameRef?.current?.scene.getScene('OceanScene') as any;
            const content = contextMenu.content;
            if (scene && content?.data?.shipId) {
              scene.engageTarget(content.data.shipId);
            }
            contextMenuEmitter.hide();
          }}>Engage</button>
          <button className="context-menu-item" onClick={() => handleExamine(getShipExamineText({
            isNPC: contextMenu.content?.data?.shipData?.isNPC,
            level: contextMenu.content?.data?.shipData?.level,
            ownerName: contextMenu.content?.data?.shipData?.ownerName,
            shipType: contextMenu.content?.data?.shipData?.shipType,
            faction: contextMenu.content?.data?.shipData?.faction,
          }))}>Examine</button>
          <button className="context-menu-item" onClick={() => contextMenuEmitter.hide()}>Cancel</button>
        </>
      )}
      {contextMenu.content.type === 'resource' && (
        <>
          <div className="context-menu-header">
            {contextMenu.content.data?.resourceNodeName || 'Resource Node'}
          </div>
          <button className="context-menu-item" onClick={() => {
            console.log('👉 Context menu: Research clicked');
            const scene = gameRef?.current?.scene.getScene('OceanScene') as any;
            const content = contextMenu.content;
            if (scene && content?.data?.resourceNodeId && content?.data?.resourceNodeType && content?.data?.resourceNodePosition) {
              scene.events.emit('resource:openResearch', {
                nodeId: content.data.resourceNodeId,
                nodeType: content.data.resourceNodeType,
                nodePosition: content.data.resourceNodePosition,
              });
            }
            contextMenuEmitter.hide();
          }}>Research</button>
          <button className="context-menu-item" onClick={() => {
            console.log('👉 Context menu: Harvest clicked');
            const scene = gameRef?.current?.scene.getScene('OceanScene') as any;
            const content = contextMenu.content;
            if (scene && content?.data?.resourceNodeId) {
              scene.harvestResourceNode(content.data.resourceNodeId);
            }
            contextMenuEmitter.hide();
          }}>Harvest</button>
          <button className="context-menu-item" onClick={() => handleExamine(getResourceNodeExamineText(contextMenu.content?.data?.resourceNodeType || 'wood'))}>Examine</button>
          <button className="context-menu-item" onClick={() => contextMenuEmitter.hide()}>Cancel</button>
        </>
      )}
      {contextMenu.content.type === 'world' && (
        <>
          <div className="context-menu-header">Travel</div>
          <button className="context-menu-item" onClick={() => {
            console.log('👉 Context menu: Travel here clicked');
            const scene = gameRef?.current?.scene.getScene('OceanScene') as any;
            const content = contextMenu.content;
            if (scene && content?.data?.worldX !== undefined && content?.data?.worldY !== undefined) {
              scene.moveToPosition(content.data.worldX, content.data.worldY);
            }
            contextMenuEmitter.hide();
          }}>Sail To</button>
          <button className="context-menu-item" onClick={() => handleExamine(getWorldExamineText())}>Examine</button>
          <button className="context-menu-item" onClick={() => contextMenuEmitter.hide()}>Cancel</button>
        </>
      )}
      {contextMenu.content.type === 'fishing' && (
        <>
          <div className="context-menu-header">
            {contextMenu.content.data?.spotType ? formatFishingSpotType(contextMenu.content.data.spotType) : 'Fishing Spot'}
          </div>
          <button className="context-menu-item" onClick={() => {
            console.log('👉 Context menu: Fish clicked');
            const scene = gameRef?.current?.scene.getScene('OceanScene') as any;
            const content = contextMenu.content;
            if (scene && content?.data?.spotId) {
              scene.startFishing({
                id: content.data.spotId,
                tileX: content.data.tileX,
                tileY: content.data.tileY,
                type: content.data.spotType,
                worldX: content.data.spotPosition?.x,
                worldY: content.data.spotPosition?.y,
              });
            }
            contextMenuEmitter.hide();
          }}>Fish</button>
          <button className="context-menu-item" onClick={() => handleExamine(getFishingSpotExamineText(contextMenu.content?.data?.spotType || 'unknown'))}>Examine</button>
          <button className="context-menu-item" onClick={() => contextMenuEmitter.hide()}>Cancel</button>
        </>
      )}
      {contextMenu.content.type === 'riftscar' && (
        <>
          <div className="context-menu-header">
            {contextMenu.content.data?.scarData?.name || 'Rift Scar'}
          </div>
          <button className="context-menu-item" onClick={() => {
            console.log('👉 Context menu: Research rift scar clicked');
            const scene = gameRef?.current?.scene.getScene('OceanScene') as any;
            const content = contextMenu.content;
            if (scene && content?.data?.scarData) {
              scene.events.emit('riftscar:openResearch', {
                scarData: content.data.scarData,
              });
            }
            contextMenuEmitter.hide();
          }}>
            {contextMenu.content.data?.scarData?.isDiscovered ? 'Research' : 'Investigate'}
          </button>
          {contextMenu.content.data?.scarData?.isDiscovered && contextMenu.content.data?.scarData?.stability !== 'sealed' && (
            <button className="context-menu-item" onClick={() => {
              console.log('👉 Context menu: Enter rift scar clicked');
              const scene = gameRef?.current?.scene.getScene('OceanScene') as any;
              const content = contextMenu.content;
              if (scene && content?.data?.scarData) {
                scene.events.emit('rift-scar-clicked', content.data.scarData);
              }
              contextMenuEmitter.hide();
            }}>Enter Rift Scar</button>
          )}
          {!contextMenu.content.data?.scarData?.isDiscovered && (
            <button className="context-menu-item" onClick={() => {
              console.log('👉 Context menu: Discover rift scar clicked');
              const scene = gameRef?.current?.scene.getScene('OceanScene') as any;
              const content = contextMenu.content;
              if (scene && content?.data?.scarData) {
                // TODO: Implement discovery mechanic
                scene.events.emit('riftscar:openResearch', {
                  scarData: content.data.scarData,
                });
              }
              contextMenuEmitter.hide();
            }}>Discover</button>
          )}
          <button className="context-menu-item" onClick={() => handleExamine(getRiftScarExamineText({
            name: contextMenu.content?.data?.scarData?.name,
            grade: contextMenu.content?.data?.scarData?.grade,
            type: contextMenu.content?.data?.scarData?.type,
          }))}>Examine</button>
          <button className="context-menu-item" onClick={() => contextMenuEmitter.hide()}>Cancel</button>
        </>
      )}
      {contextMenu.content.type === 'debris' && (
        <>
          <div className="context-menu-header">Loot Container</div>
          <button className="context-menu-item" onClick={() => {
            console.log('👉 Context menu: Open loot clicked, containerId:', contextMenu.content?.data?.containerId);
            const content = contextMenu.content;
            if (content?.data?.containerId) {
              console.log('📦 Emitting lootcontainer:open event to game instance');
              // Emit event to game instance so GameCanvas can listen for it
              gameRef?.current?.events.emit('lootcontainer:open', {
                id: content.data.containerId,
              });
            } else {
              console.error('❌ No containerId found in context menu data:', content?.data);
            }
            contextMenuEmitter.hide();
          }}>Open Loot</button>
          <button className="context-menu-item" onClick={() => handleExamine(getDebrisExamineText())}>Examine</button>
          <button className="context-menu-item" onClick={() => contextMenuEmitter.hide()}>Cancel</button>
        </>
      )}
      {contextMenu.content.type === 'sanctum_container' && (
        <>
          <div className="context-menu-header">
            {contextMenu.content.data?.containerData?.rarity ? contextMenu.content.data.containerData.rarity.toUpperCase() + ' CONTAINER' : 'Loot Container'}
          </div>
          <button className="context-menu-item" onClick={() => {
            console.log('👉 Context menu: Sanctum container open clicked, containerId:', contextMenu.content?.data?.containerId);
            const content = contextMenu.content;
            if (content?.data?.containerId) {
              const scene = gameRef?.current?.scene.getScene('OceanScene') as any;
              const localShip = scene?.localShip;
              const socket = scene?.socket;
              const containerId = content.data.containerId;
              
              // Check if this is an NPC-dropped container (starts with 'loot_') or pre-placed (starts with 'container_')
              const isNpcDropped = containerId.startsWith('loot_');
              
              if (isNpcDropped) {
                // NPC-dropped containers: use standard lootcontainer:open flow
                console.log('📦 Opening NPC-dropped container via standard flow:', containerId);
                gameRef?.current?.events.emit('lootcontainer:open', { id: containerId });
                if (socket) {
                  socket.emit('lootcontainer:open', { id: containerId });
                }
              } else {
                // Pre-placed containers: use sanctum:loot_container to generate loot
                console.log('📦 Opening pre-placed container via sanctum flow:', containerId);
                if (socket) {
                  console.log('📦 Emitting sanctum:loot_container to server');
                  socket.emit('sanctum:loot_container', {
                    instanceId: scene?.currentSanctumInstanceId,
                    containerId: containerId,
                    playerX: localShip?.x || 0,
                    playerY: localShip?.y || 0,
                  });
                }
              }
            }
            contextMenuEmitter.hide();
          }}>Open Loot</button>
          <button className="context-menu-item" onClick={() => handleExamine(getSanctumContainerExamineText(contextMenu.content?.data?.containerData?.rarity))}>Examine</button>
          <button className="context-menu-item" onClick={() => contextMenuEmitter.hide()}>Cancel</button>
        </>
      )}
      {examineModal.visible && (
        <ExamineModal
          examineData={examineModal.data}
          onClose={() => setExamineModal({ visible: false, data: null })}
        />
      )}
    </div>
  );
};

